import { Link as RouterLink, useNavigate } from 'react-router-dom'
import type { MouseEvent } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { palettePrimaryMain, paletteSuccessMain } from '@/theme/designTokens'
import { libraryCardHoverSx } from '@/shared/lib/libraryCardHover'
import { CampaignBudgetUsedIcon } from './CampaignBudgetUsedIcon'
import { CampaignCalendarPickerIcon } from './CampaignCalendarPickerIcon'
import { campaignEditHref } from '../lib/campaignResumeWizardPath'
import {
  campaignCardMetrics,
  campaignCardPrimaryHref,
  campaignCardTitle,
  formatBudgetUsed,
  formatCampaignCreatedDate,
  LANDING_STATUS_PILL,
  resolveLandingCardStatus,
  type CampaignLandingCardStatus,
} from '../lib/campaign-landing-display'
import { DYNAMIC_SURVEY_LABEL, REGULAR_SURVEY_LABEL } from '../lib/launchSurveyMetadata'
import type { Campaign, CampaignSurveyTypeTag } from '../types'

type Props = Readonly<{
  campaign: Campaign
}>

/** Figma 5110 — title block reserves two lines so row cards align below the name. */
const CAMPAIGN_CARD_TITLE_LINE_HEIGHT_PX = 22.5
const CAMPAIGN_CARD_TITLE_MAX_LINES = 2
const CAMPAIGN_CARD_TITLE_BLOCK_MIN_HEIGHT_PX =
  CAMPAIGN_CARD_TITLE_LINE_HEIGHT_PX * CAMPAIGN_CARD_TITLE_MAX_LINES

/** Figma 5110 — metric tile width ratio (px at 375px card). */
const METRIC_LOI_FR = 67
const METRIC_INSTRUMENTS_FR = 94
const METRIC_INSTRUMENTS_DRAFT_FR = 185
const METRIC_RESPONSES_FR = 145

const SURVEY_TYPE_PILL_SX: Record<CampaignSurveyTypeTag, { bgcolor: string; color: string }> = {
  [REGULAR_SURVEY_LABEL]: { bgcolor: '#e6f1fb', color: '#008cd0' },
  [DYNAMIC_SURVEY_LABEL]: { bgcolor: '#e3dffc', color: '#3f2990' },
}

function metricTilesGridColumns(showResponsesTile: boolean): string {
  if (showResponsesTile) {
    return `${METRIC_LOI_FR}fr ${METRIC_INSTRUMENTS_FR}fr ${METRIC_RESPONSES_FR}fr`
  }
  return `${METRIC_LOI_FR}fr ${METRIC_INSTRUMENTS_DRAFT_FR}fr`
}

function MetricTile({
  value,
  label,
  bg,
  border,
}: Readonly<{
  value: string | number
  label: string
  bg: string
  border: string
}>) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        height: 52,
        px: '11px',
        py: '16px',
        bgcolor: bg,
        border: `1px solid ${border}`,
        borderRadius: '8px',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '32px',
            color: '#0b1c30',
            whiteSpace: 'nowrap',
            mb: '-0.5px',
          }}
        >
          {value}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontWeight: 500,
            fontSize: '9px',
            lineHeight: '16px',
            letterSpacing: '0.12px',
            color: '#3f4850',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  )
}

function StatusPill({ status }: Readonly<{ status: CampaignLandingCardStatus }>) {
  const pill = LANDING_STATUS_PILL[status]
  return (
    <Box
      sx={{
        px: '12px',
        py: '5px',
        borderRadius: '999px',
        border: `1px solid ${pill.border}`,
        bgcolor: pill.bg,
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          color: pill.color,
        }}
      >
        {pill.label}
      </Typography>
    </Box>
  )
}

function CardActions({
  campaign,
  status,
}: Readonly<{ campaign: Campaign; status: CampaignLandingCardStatus }>) {
  const stopNav = (event: MouseEvent) => {
    event.stopPropagation()
  }

  const companyReadOnly = campaign.companyStatus === 'inactive'

  if (status === 'draft' && !companyReadOnly) {
    return (
      <Button
        component={RouterLink}
        to={campaignEditHref(campaign)}
        variant="contained"
        disableElevation
        fullWidth
        onClick={stopNav}
        data-testid={`campaign-card-continue-${campaign.id}`}
        sx={{
          borderRadius: '8px',
          height: 40,
          bgcolor: paletteSuccessMain,
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: '16px',
          textTransform: 'none',
          '&:hover': { bgcolor: '#0d9668' },
        }}
      >
        Continue Building
      </Button>
    )
  }

  const launchHref = `/campaigns/${campaign.id}`

  return (
    <Button
      component={RouterLink}
      to={launchHref}
      variant="contained"
      disableElevation
      fullWidth
      onClick={stopNav}
      data-testid={`campaign-card-launch-${campaign.id}`}
      sx={{
        borderRadius: '8px',
        height: 40,
        bgcolor: palettePrimaryMain,
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: '16px',
        textTransform: 'none',
        '&:hover': { bgcolor: '#0077b3' },
      }}
    >
      {companyReadOnly && status === 'draft' ? 'View Campaign' : 'View Launching Screen'}
    </Button>
  )
}

export function CampaignCard({ campaign }: Props) {
  const navigate = useNavigate()
  const status = resolveLandingCardStatus(campaign)
  const primaryHref = campaignCardPrimaryHref(campaign)
  const title = campaignCardTitle(campaign)
  const { loiCount, instrumentCount, totalResponses } = campaignCardMetrics(campaign)
  const showResponsesTile = status !== 'draft'
  const surveyTypeTags = campaign.surveyTypeTags ?? []
  const companyName = campaign.companyName?.trim() ?? ''
  const metricTiles = [
    { key: 'lois', value: loiCount, label: 'LOIs', bg: '#e7fbff', border: '#c7ebf9' },
    {
      key: 'instruments',
      value: instrumentCount,
      label: 'Instruments',
      bg: '#ffebee',
      border: '#ffd9ea',
    },
    ...(showResponsesTile
      ? [
          {
            key: 'responses',
            value: totalResponses,
            label: 'Total instrument Responses',
            bg: 'rgba(245, 158, 11, 0.2)',
            border: '#fbe797',
          },
        ]
      : []),
  ]

  return (
    <Box
      data-testid={`campaign-card-${campaign.id}`}
      onClick={() => navigate(primaryHref)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(primaryHref)
        }
      }}
      role="link"
      tabIndex={0}
      aria-label={`Open ${title}`}
      sx={{
        bgcolor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        p: '24px',
        height: '100%',
        minHeight: 292,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        ...libraryCardHoverSx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: '12px',
          minHeight: companyName ? undefined : `${CAMPAIGN_CARD_TITLE_BLOCK_MIN_HEIGHT_PX}px`,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
          <Tooltip title={title} placement="top-start">
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: `${CAMPAIGN_CARD_TITLE_LINE_HEIGHT_PX}px`,
                color: '#0f172a',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: CAMPAIGN_CARD_TITLE_MAX_LINES,
                WebkitBoxOrient: 'vertical',
                minHeight: companyName
                  ? undefined
                  : `${CAMPAIGN_CARD_TITLE_BLOCK_MIN_HEIGHT_PX}px`,
              }}
            >
              {title}
            </Typography>
          </Tooltip>
          {companyName ? (
            <Typography
              data-testid={`campaign-card-company-${campaign.id}`}
              sx={{
                mt: '4px',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#565656',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {companyName}
            </Typography>
          ) : null}
        </Box>
        <StatusPill status={status} />
      </Box>

      {surveyTypeTags.length > 0 ? (
        <Box
          data-testid={`campaign-card-survey-types-${campaign.id}`}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            mb: '12px',
          }}
        >
          {surveyTypeTags.map((tag) => {
            const pill = SURVEY_TYPE_PILL_SX[tag]
            return (
              <Box
                key={tag}
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 26,
                  px: '14px',
                  borderRadius: '9999px',
                  bgcolor: pill.bgcolor,
                  color: pill.color,
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  whiteSpace: 'nowrap',
                }}
              >
                {tag}
              </Box>
            )
          })}
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: metricTilesGridColumns(showResponsesTile),
          gap: '10px',
          width: '100%',
          mb: '22px',
        }}
      >
        {metricTiles.map((tile) => (
          <MetricTile
            key={tile.key}
            value={tile.value}
            label={tile.label}
            bg={tile.bg}
            border={tile.border}
          />
        ))}
      </Box>

      <Box
        sx={{
          bgcolor: '#eef9ff',
          border: '1px solid #e1e7f3',
          borderRadius: '8px',
          px: '10px',
          py: '10px',
          mb: '22px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '86px', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CampaignBudgetUsedIcon sx={{ fontSize: 16, color: '#10b981' }} />
              <Typography
                sx={{ fontWeight: 700, fontSize: '12px', lineHeight: '24px', color: '#10b981' }}
              >
                {formatBudgetUsed(campaign.budgetUsed)}
              </Typography>
            </Box>
            <Typography
              sx={{
                mt: '2px',
                fontSize: '9px',
                lineHeight: '14px',
                color: '#04183f',
                textTransform: 'uppercase',
              }}
            >
              Budget used
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CampaignCalendarPickerIcon sx={{ fontSize: 12, color: '#3b82f6' }} />
              <Typography
                sx={{ fontWeight: 700, fontSize: '12px', lineHeight: '24px', color: '#3b82f6' }}
              >
                {formatCampaignCreatedDate(campaign.createdAt)}
              </Typography>
            </Box>
            <Typography
              sx={{
                mt: '2px',
                fontSize: '9px',
                lineHeight: '14px',
                color: '#04183f',
                textTransform: 'uppercase',
              }}
            >
              Created date
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <CardActions campaign={campaign} status={status} />
      </Box>
    </Box>
  )
}
